// hooks/features/apiKeys/useApiKeys.ts

import { useState, useCallback, useEffect } from "react";
import useToken from "../../../hooks/features/auth/useToken";
import { ApiKey, CreateApiKeyRequest } from "../../../types/features/ApiKey";
import { apiKeyService } from "../../../services/apiKeyService";

export default function useApiKeys() {
  const { token } = useToken();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [selectedApiKey, setSelectedApiKey] = useState<ApiKey | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getApiKeys = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const response = await apiKeyService.getApiKeys(token);
      setApiKeys(response);
    } catch (err) {
      setError("Erreur lors du chargement des clés API");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const createApiKey = useCallback(async (
    data: CreateApiKeyRequest
  ): Promise<ApiKey | null> => {
    if (!token) return null;
    setLoading(true);
    setError(null);
    try {
      const response = await apiKeyService.createApiKey(token, data);
      const newApiKey = {
        id: response.data.id,
        name: response.data.name,
        module: response.data.module,
        key: response.data.key,
        permissions: response.data.permissions,
        expires_at: response.data.expires_at,
        last_used_at: null,
        is_active: true,
        created_at: new Date().toISOString(),
      };
      setApiKeys((prev) => [newApiKey, ...prev]);
      return newApiKey;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Erreur lors de la création de la clé API";
      setError(errorMessage);
      console.error(err.response);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  const regenerateApiKey = useCallback(async (id: string): Promise<string | null> => {
    if (!token) return null;
    setLoading(true);
    setError(null);
    try {
      const response = await apiKeyService.regenerateApiKey(token, id);
      setApiKeys((prev) => 
        prev.map((key) => 
          key.id === id 
            ? { ...key, key: response.key.substring(0, 8) + '...' + response.key.substring(response.key.length - 8) }
            : key
        )
      );
      if (selectedApiKey?.id === id) {
        setSelectedApiKey({ ...selectedApiKey, key: response.key });
      }
      return response.key;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Erreur lors de la régénération de la clé";
      setError(errorMessage);
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token, selectedApiKey]);

  const deleteApiKey = useCallback(async (id: string): Promise<boolean> => {
    if (!token) return false;
    setLoading(true);
    setError(null);
    try {
      await apiKeyService.deleteApiKey(token, id);
      setApiKeys((prev) => prev.filter((key) => key.id !== id));
      if (selectedApiKey?.id === id) setSelectedApiKey(null);
      return true;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Erreur lors de la suppression de la clé";
      setError(errorMessage);
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [token, selectedApiKey]);

  const toggleApiKey = useCallback(async (id: string): Promise<boolean> => {
    if (!token) return false;
    setLoading(true);
    setError(null);
    try {
      const response = await apiKeyService.toggleApiKey(token, id);
      setApiKeys((prev) => 
        prev.map((key) => 
          key.id === id ? { ...key, is_active: response.is_active } : key
        )
      );
      if (selectedApiKey?.id === id) {
        setSelectedApiKey({ ...selectedApiKey, is_active: response.is_active });
      }
      return true;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Erreur lors du changement de statut";
      setError(errorMessage);
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [token, selectedApiKey]);

  const updatePermissions = useCallback(async (id: string, permissions: string[]): Promise<boolean> => {
    if (!token) return false;
    setLoading(true);
    setError(null);
    try {
      const response = await apiKeyService.updatePermissions(token, id, permissions);
      setApiKeys((prev) => 
        prev.map((key) => 
          key.id === id ? { ...key, permissions: response.permissions } : key
        )
      );
      if (selectedApiKey?.id === id) {
        setSelectedApiKey({ ...selectedApiKey, permissions: response.permissions });
      }
      return true;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Erreur lors de la mise à jour des permissions";
      setError(errorMessage);
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [token, selectedApiKey]);

  useEffect(() => {
    getApiKeys();
  }, [getApiKeys]);

  return {
    apiKeys,
    selectedApiKey,
    setSelectedApiKey,
    loading,
    error,
    setError,
    getApiKeys,
    createApiKey,
    regenerateApiKey,
    deleteApiKey,
    toggleApiKey,
    updatePermissions,
  };
}